import {
    hasMeaningfulCheckCoordinates,
    isValidCheckRadius,
    normalizeCheckAddress,
    normalizeCheckCountryCode,
    toCheckLocationNumber,
} from "components/events/helpers/event-check-location-helper";
import { STAFF_CHECK_QR_STATUS } from "./staff-check-qr-helper";


export const buildCheckInLocationInput = (eventID, values = {}) => ({
    id: Number(eventID),
    check_country: normalizeCheckCountryCode(values.check_country),
    check_address: normalizeCheckAddress(values.check_address),
    check_latitude: values.check_latitude,
    check_longitude: values.check_longitude,
    check_radius: values.check_radius,
});

export const normalizeRoleIds = (roleIds = []) => (
    [ ...new Set(
        (roleIds || [])
            .map((role) => role?.id ?? role)
            .filter((roleID) => roleID !== null && roleID !== undefined && roleID !== "")
            .map(String)
    ) ]
);

export const getEventRoleIds = (event) => normalizeRoleIds(event?.relWorkerRoles);

// Group IDs are synchronized between dev/live; their display names can differ.
const DEFAULT_CHECK_IN_ROLE_IDS = [ "8", "15", "3" ];

const getCheckInEntityID = (id) => {
    if (typeof id === "string") return id.trim();
    return Number.isInteger(id) && id > 0 ? String(id) : "";
};

const isEmptyCheckInText = (value) => (
    value === null || value === undefined || (typeof value === "string" && !value.trim())
);

const isEmptyCheckInNumber = (value) => (
    isEmptyCheckInText(value) ||
    ((typeof value === "number" || typeof value === "string") && Number(value) === 0)
);

export const isEmptyCheckInSetup = (event) => (
    Boolean(getCheckInEntityID(event?.id)) &&
    (event.relWorkerRoles === null || event.relWorkerRoles === undefined ||
        (Array.isArray(event.relWorkerRoles) && getEventRoleIds(event).length === 0)) &&
    isEmptyCheckInText(event.check_address) &&
    isEmptyCheckInText(event.getCheckQrCodeUrl) &&
    // The backend can supply a radius before any check-in point is configured.
    [ event.check_latitude, event.check_longitude ].every(isEmptyCheckInNumber)
);

export const getDefaultCheckInRoleIds = (roles = []) => {
    const availableIds = new Set(
        (Array.isArray(roles) ? roles : []).map((role) => getCheckInEntityID(role?.id))
    );

    return DEFAULT_CHECK_IN_ROLE_IDS.filter((id) => availableIds.has(id));
};

export const getExhibitionWorkerCount = (role) => {
    const rawCount = role?.getExhibitionWorkersCount;

    if (rawCount === null || rawCount === undefined)
    {
        return null;
    }

    const count = Number(rawCount);

    return Number.isInteger(count) && count >= 0 ? count : null;
};

export const getRoleIdsSnapshot = (roleIds) => normalizeRoleIds(roleIds)
    .sort((firstRoleID, secondRoleID) => firstRoleID.localeCompare(secondRoleID))
    .join("|");

export const getCheckInLocationSnapshot = (location = {}) => JSON.stringify([
    normalizeCheckCountryCode(location.check_country, ""),
    normalizeCheckAddress(location.check_address),
    toCheckLocationNumber(location.check_latitude),
    toCheckLocationNumber(location.check_longitude),
    toCheckLocationNumber(location.check_radius),
]);

export const hasValidCheckInLocation = (location = {}) => (
    hasMeaningfulCheckCoordinates(location.check_latitude, location.check_longitude) &&
    isValidCheckRadius(location.check_radius)
);

export const isCheckInSetupComplete = ({ location, roleIds }) => (
    hasValidCheckInLocation(location) && normalizeRoleIds(roleIds).length > 0
);

export const getCheckInSetupSavePlan = ({ event, values = {}, qrStatus }) => {
    const locationInput = buildCheckInLocationInput(event?.id, values);
    const roleIds = normalizeRoleIds(values.role_id);
    const previousRoleIds = getEventRoleIds(event);
    const nextSetupComplete = isCheckInSetupComplete({ location: locationInput, roleIds });

    return {
        locationInput,
        roleIds,
        locationChanged: getCheckInLocationSnapshot(event) !== getCheckInLocationSnapshot(locationInput),
        rolesChanged: getRoleIdsSnapshot(previousRoleIds) !== getRoleIdsSnapshot(roleIds),
        previousSetupComplete: isCheckInSetupComplete({
            location: event,
            roleIds: previousRoleIds,
        }),
        nextSetupComplete,
        shouldGenerateFirstQr: Boolean(
            !event?.getCheckQrCodeUrl &&
            nextSetupComplete &&
            qrStatus === STAFF_CHECK_QR_STATUS.MISSING
        ),
    };
};

export const mergeSetupEvent = (currentEvent, updatedEvent) => {
    const definedUpdates = Object.entries(updatedEvent || {}).reduce((updates, [ key, value ]) => (
        value === undefined ? updates : { ...updates, [ key ]: value }
    ), {});

    return { ...currentEvent, ...definedUpdates };
};

export const getMatchingSetupEvent = (payload, eventID) => {
    const updatedEvent = payload?.exhibitionEvent;

    return updatedEvent && String(updatedEvent.id) === String(eventID) ? updatedEvent : null;
};

export const executeCheckInSetupSave = async ({
    event,
    eventID,
    generateFirstQr,
    isActive = () => true,
    onPersist = () => {},
    plan,
    updateLocation,
    updateRoles,
}) => {
    let savedEvent = event;

    if (plan.locationChanged)
    {
        const locationResult = await updateLocation(plan.locationInput);
        const locationEvent = getMatchingSetupEvent(
            locationResult?.data?.eeUpdate,
            eventID
        );

        if (!locationEvent || !isActive()) return { completed: false, savedEvent };

        savedEvent = mergeSetupEvent(savedEvent, locationEvent);
        onPersist(savedEvent);
    }

    if (plan.rolesChanged)
    {
        const rolesResult = await updateRoles(plan.roleIds);
        const rolesEvent = getMatchingSetupEvent(
            rolesResult?.data?.exhibitionWorkerRoleUpdate,
            eventID
        );

        if (!rolesEvent || !isActive()) return { completed: false, savedEvent };

        savedEvent = mergeSetupEvent(savedEvent, rolesEvent);
        onPersist(savedEvent);
    }

    if (
        plan.shouldGenerateFirstQr &&
        !savedEvent?.getCheckQrCodeUrl &&
        isActive()
    )
    {
        const generatedQrUrl = await generateFirstQr(savedEvent);

        if (generatedQrUrl && isActive())
        {
            savedEvent = mergeSetupEvent(savedEvent, {
                getCheckQrCodeUrl: generatedQrUrl,
            });
            onPersist(savedEvent);
        }
    }

    return { completed: isActive(), savedEvent };
};
